/**
 * Agent 数据概览管理器
 * 负责 Agent 数据概览的业务逻辑
 * @author Joey
 */

import { useState, useEffect, useRef, useMemo } from 'react'
import { useNavigate, useParams } from "react-router-dom";
import * as echarts from 'echarts'
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from 'react-i18next';
import { hourlyGenerationStats, StatsItemsData } from '@/services/agent-v2-user-stats-hourly-generation';
import { contentTypeDistribution, ContentTypeItemsData } from '@/services/agent-v2-user-stats-content-type-distribution';
import dayjs from 'dayjs';
import { agentV2UserStatsOverview, AgentV2UserStatsOverviewData } from '@/services/agent-v2-user-stats-overview';
import { topActiveUsers, TopActiveUsersItemsData } from '@/services/agent-v2-user-stats-top-active';
import { trendGenerationStats, TrendStatsItemsData } from '@/services/agent-v2-user-stats-trend';

type FilterDateChangeEventDetail = { filterDate: number };

export class AgentDataOverviewManager {
  private static instance: AgentDataOverviewManager;
  private readonly STORAGE_KEY = "MIUTA_AGENT_DATA_OVERVIEW";
  private agentId = null;
  private filterDate = 7
  // 统计数据初始化状态
  private initializeStats = false;
  // 图表统计初始化状态
  private analysisInitialize = false;

  // 用户数据概览数据
  private userStatsOverviewData: AgentV2UserStatsOverviewData = {} as AgentV2UserStatsOverviewData;
  // 24小时生成统计数据
  private hourlyGenerationStatsData: StatsItemsData[] = [];
  // 日活用户 & 生成条数趋势
  private trendGenerationStatsData: TrendStatsItemsData[] = [];
  // 内容类型分布数据
  private contentTypeDistributionData: ContentTypeItemsData[] = [];
  // 活跃用户TOP10
  private activeTopUsers: TopActiveUsersItemsData[] = [];

  private constructor() { }

  // 单例模式
  public static getInstance(): AgentDataOverviewManager {
    if (!AgentDataOverviewManager.instance) {
      AgentDataOverviewManager.instance = new AgentDataOverviewManager();
    }
    return AgentDataOverviewManager.instance;
  }

  // 初始化
  public async init() {

  }

  // 获取24小时生成统计数据
  public async fetchHourlyGenerationStatsData() {
    try {
      const data = await hourlyGenerationStats({
        // 始终获取昨日的日期
        date: dayjs().subtract(1, 'day').format('YYYY-MM-DD'),
        agentId: this.getAgentId(),
      });
      this.setHourlyGenerationStatsData(data.items);
    } catch (error) {
      console.error("Failed to fetch agent hourly generation stats data:", error);
      this.dispatchHourlyGenerationStatsChange();
      throw error;
    }
  }

  // 获取日活用户 & 生成条数趋势数据
  public async fetchTrendGenerationStatsData() {
    try {
      const data = await trendGenerationStats({
        recentDays: this.getFilterDate(),
        agentId: this.getAgentId(),
      });
      this.setTrendGenerationStatsData(data.items);
    } catch (error) {
      console.error("Failed to fetch agent trend generation stats data:", error);
      this.dispatchTrendGenerationStatsChange();
      throw error;
    }
  }

  // 获取内容类型分布数据
  public async fetchContentTypeDistributionData() {
    try {
      const data = await contentTypeDistribution({
        recentDays: this.getFilterDate(),
        agentId: this.getAgentId(),
      });
      this.setContentTypeDistributionData(data.items);
    } catch (error) {
      console.error("Failed to fetch agent content type distribution data:", error);
      this.dispatchContentTypeDistributionChange();
      throw error;
    }
  }

  // 获取用户数据概览数据
  public async fetchUserStatsOverviewData() {
    try {
      const data = await agentV2UserStatsOverview(this.agentId);
      this.setUserStatsOverviewData(data);
    } catch (error) {
      console.error("Failed to fetch agent user stats overview data:", error);
      this.dispatchUserStatsOverviewChange();
      throw error;
    }
  }

  // 获取活跃用户TOP10数据
  public async fetchActiveTopUsers() {
    try {
      const data = await topActiveUsers({
        agentId: this.getAgentId(),
        recentDays: this.getFilterDate(),
        limit: 10, // 固定前10
      });
      this.setActiveTopUsers(data.items);
    } catch (error) {
      console.error("Failed to fetch agent active tops data:", error);
      this.dispatchActiveTopUsersChange();
      throw error;
    }
  }

  // 重置列表和搜索表单
  public resetDataOverview() {
    this.setHourlyGenerationStatsData([]);
    this.setContentTypeDistributionData([]);
    this.setActiveTopUsers([]);
    this.setUserStatsOverviewData({} as AgentV2UserStatsOverviewData);
    // 重置统计数据
    this.setInitializeStats(false);
    // 重置图表统计
    this.setAnalysisInitialize(false);
  }

  // ==================== 读取写入操作 ====================
  public getFilterDate() {
    return this.filterDate
  }
  public setFilterDate(filterDate: number) {
    this.filterDate = filterDate
    this.dispatchFilterDateChange()
  }

  public getAgentId() {
    return this.agentId
  }
  public setAgentId(agentId: string) {
    this.agentId = agentId
  }

  public getInitializeStats() {
    return this.initializeStats;
  }
  public setInitializeStats(initializeStats: boolean) {
    this.initializeStats = initializeStats;
  }

  public getAnalysisInitialize() {
    return this.analysisInitialize;
  }
  public setAnalysisInitialize(analysisInitialize: boolean) {
    this.analysisInitialize = analysisInitialize;
  }

  public getHourlyGenerationStatsData() {
    return this.hourlyGenerationStatsData
  }
  public setHourlyGenerationStatsData(hourlyGenerationStatsData: StatsItemsData[]) {
    this.hourlyGenerationStatsData = hourlyGenerationStatsData
    this.dispatchHourlyGenerationStatsChange()
  }

  public getContentTypeDistributionData() {
    return this.contentTypeDistributionData
  }
  public setContentTypeDistributionData(contentTypeDistributionData: ContentTypeItemsData[]) {
    this.contentTypeDistributionData = contentTypeDistributionData
    this.dispatchContentTypeDistributionChange()
  }

  public getUserStatsOverviewData() {
    return this.userStatsOverviewData
  }
  public setUserStatsOverviewData(userStatsOverviewData: AgentV2UserStatsOverviewData) {
    this.userStatsOverviewData = userStatsOverviewData
    this.dispatchUserStatsOverviewChange()
  }

  public getActiveTopUsers() {
    return this.activeTopUsers
  }
  public setActiveTopUsers(activeTopUsers: TopActiveUsersItemsData[]) {
    this.activeTopUsers = activeTopUsers
    this.dispatchActiveTopUsersChange()
  }

  public getTrendGenerationStatsData() {
    return this.trendGenerationStatsData
  }
  public setTrendGenerationStatsData(trendGenerationStatsData: TrendStatsItemsData[]) {
    this.trendGenerationStatsData = trendGenerationStatsData
    this.dispatchTrendGenerationStatsChange()
  }

  // ==================== 订阅通知操作 ====================
  // 监听日期变化事件
  private dispatchFilterDateChange(): void {
    const event = new CustomEvent<FilterDateChangeEventDetail>(`${this.STORAGE_KEY}:filter-date-change`, {
      detail: { filterDate: this.filterDate }
    });
    window.dispatchEvent(event);
  }
  public onFilterDateChange(listener: (e: CustomEvent<FilterDateChangeEventDetail>) => void) {
    window.addEventListener(`${this.STORAGE_KEY}:filter-date-change`, listener);
    return () => window.removeEventListener(`${this.STORAGE_KEY}:filter-date-change`, listener);
  }

  // 监听24小时生成统计数据变化事件
  private dispatchHourlyGenerationStatsChange(): void {
    const event = new CustomEvent<StatsItemsData[]>(`${this.STORAGE_KEY}:hourly-generation-stats-change`, {
      detail: this.hourlyGenerationStatsData
    });
    window.dispatchEvent(event);
  }
  public onHourlyGenerationStatsChange(listener: (e: CustomEvent<StatsItemsData[]>) => void) {
    window.addEventListener(`${this.STORAGE_KEY}:hourly-generation-stats-change`, listener);
    return () => window.removeEventListener(`${this.STORAGE_KEY}:hourly-generation-stats-change`, listener);
  }

  // 监听内容类型分布数据变化事件
  private dispatchContentTypeDistributionChange(): void {
    const event = new CustomEvent<ContentTypeItemsData[]>(`${this.STORAGE_KEY}:content-type-distribution-change`, {
      detail: this.contentTypeDistributionData
    });
    window.dispatchEvent(event);
  }
  public onContentTypeDistributionChange(listener: (e: CustomEvent<ContentTypeItemsData[]>) => void) {
    window.addEventListener(`${this.STORAGE_KEY}:content-type-distribution-change`, listener);
    return () => window.removeEventListener(`${this.STORAGE_KEY}:content-type-distribution-change`, listener);
  }

  // 监听用户数据概览数据变化事件
  private dispatchUserStatsOverviewChange(): void {
    const event = new CustomEvent<AgentV2UserStatsOverviewData>(`${this.STORAGE_KEY}:user-stats-overview-change`, {
      detail: this.userStatsOverviewData
    });
    window.dispatchEvent(event);
  }
  public onUserStatsOverviewChange(listener: (e: CustomEvent<AgentV2UserStatsOverviewData>) => void) {
    window.addEventListener(`${this.STORAGE_KEY}:user-stats-overview-change`, listener);
    return () => window.removeEventListener(`${this.STORAGE_KEY}:user-stats-overview-change`, listener);
  }

  // 监听活跃用户TOP10变化事件
  private dispatchActiveTopUsersChange(): void {
    const event = new CustomEvent<TopActiveUsersItemsData[]>(`${this.STORAGE_KEY}:active-top-users-change`, {
      detail: this.activeTopUsers
    });
    window.dispatchEvent(event);
  }
  public onActiveTopUsersChange(listener: (e: CustomEvent<TopActiveUsersItemsData[]>) => void) {
    window.addEventListener(`${this.STORAGE_KEY}:active-top-users-change`, listener);
    return () => window.removeEventListener(`${this.STORAGE_KEY}:active-top-users-change`, listener);
  }

  // 监听日活用户 & 生成条数趋势变化事件
  private dispatchTrendGenerationStatsChange(): void {
    const event = new CustomEvent<TrendStatsItemsData[]>(`${this.STORAGE_KEY}:trend-generation-stats-change`, {
      detail: this.trendGenerationStatsData
    });
    window.dispatchEvent(event);
  }
  public onTrendGenerationStatsChange(listener: (e: CustomEvent<TrendStatsItemsData[]>) => void) {
    window.addEventListener(`${this.STORAGE_KEY}:trend-generation-stats-change`, listener);
    return () => window.removeEventListener(`${this.STORAGE_KEY}:trend-generation-stats-change`, listener);
  }
}

// ============= Hooks =============
// 聚合统计数据
const useUserStatsOverviewData = () => {
  const toast = useToast();
  const { id } = useParams();

  const [loading, setLoading] = useState(false)
  const [userStatsOverviewData, setUserStatsOverviewData] = useState(agentDataOverviewManager.getUserStatsOverviewData())

  const fetchUserStatsOverviewData = async () => {
    try {
      setLoading(true);
      // 初始化
      agentDataOverviewManager.setInitializeStats(true)
      // 请求用户数据概览数据
      await agentDataOverviewManager.fetchUserStatsOverviewData()
    } catch (error) {
      console.error("Failed to fetch agent user stats overview data:", error);
      toast.showToastByCode(error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    agentDataOverviewManager.setAgentId(id)
    // 如果已经初始化了统计数据，就不需要再初始化
    if (agentDataOverviewManager.getInitializeStats()) return
    fetchUserStatsOverviewData()
  }, [id])

  useEffect(() => {
    const unsubscribe = agentDataOverviewManager.onUserStatsOverviewChange((e) => {
      setUserStatsOverviewData(e.detail)
    })
    return unsubscribe
  })

  return {
    loading,
    userStatsOverviewData,
    fetchUserStatsOverviewData
  }
}

// 图表数据概览
const useDataAnalysis = () => {
  const toast = useToast();
  const { id } = useParams();

  const [loading, setLoading] = useState(false);
  const [filterDate, setFilterDate] = useState(agentDataOverviewManager.getFilterDate())

  // 请求统计数据
  const fetchAnalysisData = async () => {
    try {
      setLoading(true);

      await Promise.all([
        agentDataOverviewManager.fetchContentTypeDistributionData(),
        agentDataOverviewManager.fetchActiveTopUsers(),
        agentDataOverviewManager.fetchTrendGenerationStatsData(),
      ])
    } catch (error) {
      console.error("Failed to fetch agent data analysis stats data:", error);
      toast.showToastByCode(error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    // 初始化时设置 agentId
    agentDataOverviewManager.setAgentId(id)
    if (!agentDataOverviewManager.getAnalysisInitialize()) {
      agentDataOverviewManager.setAnalysisInitialize(true)
      fetchAnalysisData()
    }
  }, [id])

  const changeFilterDate = (filterDate: number) => {
    agentDataOverviewManager.setFilterDate(filterDate)
    fetchAnalysisData()
  }

  useEffect(() => {
    const unsubscribe = agentDataOverviewManager.onFilterDateChange((e) => {
      setFilterDate(e.detail.filterDate)
    })
    return unsubscribe
  }, [])

  return {
    loading,
    filterDate,
    changeFilterDate
  }
}

// 折线图（日活用户 & 生成条数趋势图表）
const useLineChart = () => {
  const { t } = useTranslation()

  const [trendGenerationStatsData, setTrendGenerationStatsData] = useState(agentDataOverviewManager.getTrendGenerationStatsData())

  const lineChartRef = useRef<HTMLDivElement>(null)
  const chartInstanceRef = useRef<echarts.ECharts | null>(null)

  const { dates, activeUsers, generatedItems } = useMemo(() => {
    if (!trendGenerationStatsData) return {}
    const dates = trendGenerationStatsData.map(item => item.date)
    const activeUsers = trendGenerationStatsData.map(item => item.activeUsers)
    const generatedItems = trendGenerationStatsData.map(item => item.totalProduct)
    return { dates, activeUsers, generatedItems }
  }, [trendGenerationStatsData])

  useEffect(() => {
    if (!lineChartRef.current) return

    // 初始化图表实例
    if (!chartInstanceRef.current) {
      chartInstanceRef.current = echarts.init(lineChartRef.current)
    }

    // 图表配置
    const option: echarts.EChartsOption = {
      // title: {
      //   text: '日活用户 & 生成条数趋势',
      //   left: 'left',
      //   textStyle: {
      //     fontSize: 16,
      //     fontWeight: 'normal'
      //   }
      // },
      tooltip: {
        trigger: 'axis',
        formatter: function (params) {
          let result = params[0].name + '<br/>'
          params.forEach((item) => {
            result += item.marker + item.seriesName + ': ' + item.value + '<br/>'
          })
          return result
        }
      },
      legend: {
        data: [
          t('agent_dashboard.data_overview.data_analysis.line_chart.active_user'),
          t('agent_dashboard.data_overview.data_analysis.line_chart.generated_count')
        ],
        icon: 'circle', // 将图例图标设置为圆形
        bottom: 0,
        top: 10,
        right: 30,
        itemWidth: 9,
        itemHeight: 9,
        itemGap: 15,
        textStyle: {
          fontSize: 15
        }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '15%',
        top: '15%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: dates,
        boundaryGap: false,
        axisLine: {
          lineStyle: {
            color: '#E5E7EB'
          }
        },
        axisTick: {
          show: false
        },
        axisLabel: {
          fontSize: 12
        }
      },
      yAxis: {
        type: 'value',
        axisLine: {
          show: false
        },
        axisTick: {
          show: false
        },
        splitLine: {
          lineStyle: {
            color: '#F3F4F6',
            type: 'dashed'
          }
        },
        axisLabel: {
          fontSize: 12
        }
      },
      series: [
        {
          name: t('agent_dashboard.data_overview.data_analysis.line_chart.active_user'),
          type: 'line',
          data: activeUsers,
          smooth: true,
          symbol: 'circle',
          symbolSize: 6,
          lineStyle: {
            width: 2,
            color: '#7F56D9' // 紫色线条
          },
          itemStyle: {
            color: '#7F56D9'
          },
          areaStyle: {
            opacity: 1,
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              {
                offset: 0,
                color: 'rgba(127,86,217,0.6)'
              },
              {
                offset: 1,
                color: 'rgba(127,86,217,0.05)'
              }
            ])
          },
        },
        {
          name: t('agent_dashboard.data_overview.data_analysis.line_chart.generated_count'),
          type: 'line',
          data: generatedItems,
          smooth: true,
          symbol: 'circle',
          symbolSize: 6,
          lineStyle: {
            width: 2,
            color: '#B692F6' // 紫色线条
          },
          itemStyle: {
            color: '#B692F6'
          },
          areaStyle: {
            opacity: 1,
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              {
                offset: 0,
                color: 'rgba(147,51,234,0.6)'
              },
              {
                offset: 1,
                color: 'rgba(147,51,234,0.05)'
              }
            ])
          }
        }
      ]
    }

    // 设置图表配置
    chartInstanceRef.current.setOption(option)

    // 响应式调整
    const handleResize = () => {
      chartInstanceRef.current?.resize()
    }

    window.addEventListener('resize', handleResize)

    // 清理函数
    return () => {
      window.removeEventListener('resize', handleResize)
      // 注意：这里不销毁实例，以便在日期切换时复用
    }
  }, [dates, activeUsers, generatedItems])

  useEffect(() => {
    const unsubscribe = agentDataOverviewManager.onTrendGenerationStatsChange((e) => {
      setTrendGenerationStatsData(e.detail)
    })
    return unsubscribe
  }, [])

  // 组件卸载时销毁图表实例
  useEffect(() => {
    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.dispose()
        chartInstanceRef.current = null
      }
    }
  }, [])

  return {
    lineChartRef,
  }
}

// 饼状图（内容分布类型）
const usePieChart = () => {
  const { t } = useTranslation()
  const routeParams = useParams();
  const navigate = useNavigate()
  const pieChartRef = useRef<HTMLDivElement>(null)
  const chartInstanceRef = useRef<echarts.ECharts | null>(null)

  const [contentTypeDistributionData, setContentTypeDistributionData] = useState(agentDataOverviewManager.getContentTypeDistributionData())

  // 组装图表数据
  const data = useMemo(() => {
    const list = contentTypeDistributionData.map(item => {
      return {
        value: item.count,
        name: item.typeName,
        type: item.type
      }
    })

    return list;
  }, [contentTypeDistributionData])

  // 处理饼图元素点击事件
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handlePieChartClick = (params: any) => {
    // 前端内容管理页
    navigate(`/agent/dashboard/content-management/${routeParams.id}/?type=${params.data.type}`)
  }

  useEffect(() => {
    if (!pieChartRef.current) return

    // 初始化图表实例
    if (!chartInstanceRef.current) {
      chartInstanceRef.current = echarts.init(pieChartRef.current)
      
      // 绑定点击事件
      chartInstanceRef.current.on('click', handlePieChartClick)
    }

    // 计算百分比
    const total = data.reduce((sum, item) => sum + item.value, 0)
    const dataWithPercentage = data.map(item => ({
      ...item,
      percentage: Math.round((item.value / total) * 100)
    }))

    // 图表配置
    const option: echarts.EChartsOption = {
      // title: {
      //   text: '内容类型分布',
      //   left: 'left',
      //   textStyle: {
      //     fontSize: 16,
      //     fontWeight: 'normal',
      //   }
      // },
      tooltip: {
        trigger: 'item',
        formatter: '{a} <br/>{b}: {c} ({d}%)'
      },
      legend: {
        orient: 'vertical',
        right: 100,
        top: 60,
        textStyle: {
          fontSize: 16,
        },
        icon: 'circle', // 将图例图标设置为圆形
        itemWidth: 13,
        itemHeight: 13,
        itemGap: 12
      },
      series: [
        {
          name: '内容类型',
          type: 'pie',
          radius: ['40%', '70%'], // 甜甜圈样式
          center: ['40%', '50%'], // 调整中心位置
          data: dataWithPercentage.map(item => ({
            value: item.value,
            name: `${item.name}`,
            type: item.type,
            label: {
              show: false // 不显示标签
            }
          })),
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)'
            }
          },
          // 间距
          itemStyle: {
            borderRadius: 4,
            borderColor: '#fff',
            borderWidth: 2
          },
          color: [
            '#45AEFF',
            '#814FFF',
            '#FFC054',
            '#FF32A3',
            '#33FFFF',
            '#2C84FF'
          ]
        }
      ]
    }

    // 设置图表配置
    chartInstanceRef.current.setOption(option)

    // 响应式调整
    const handleResize = () => {
      chartInstanceRef.current?.resize()
    }

    window.addEventListener('resize', handleResize)

    // 清理函数
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [contentTypeDistributionData, data])

  useEffect(() => {
    const unsubscribe = agentDataOverviewManager.onContentTypeDistributionChange((e) => {
      setContentTypeDistributionData(e.detail)
    })
    return unsubscribe
  })

  // 组件卸载时销毁图表实例
  useEffect(() => {
    return () => {
      if (chartInstanceRef.current) {
        // 移除事件监听器
        chartInstanceRef.current.off('click', handlePieChartClick)
        chartInstanceRef.current.dispose()
        chartInstanceRef.current = null
      }
    }
  }, [])

  return {
    pieChartRef
  }
}

// 柱状图（24小时生成趋势）
const useBarChart = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const barChartRef = useRef<HTMLDivElement>(null);
  const chartInstanceRef = useRef<echarts.ECharts | null>(null);

  const [hourlyGenerationStatsData, setHourlyGenerationStatsData] = useState(agentDataOverviewManager.getHourlyGenerationStatsData())

  // 生成 24 小时数据的标签
  const hours = Array.from({ length: 24 }, (_, hour) => {
    return `${hour.toString().padStart(2, '0')}:00`;
  });;

  const fetchHourlyGenerationStatsData = async () => {
    try {
      setLoading(true);
      await agentDataOverviewManager.fetchHourlyGenerationStatsData();
    } catch (error) {
      console.error("Hourly Generation Stats data fetch failed:", error.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (agentDataOverviewManager.getHourlyGenerationStatsData().length > 0) return;
    fetchHourlyGenerationStatsData();
  }, [])

  // 生成 24 小时数据的值
  const hourValues = useMemo(() => hourlyGenerationStatsData.map(item => item.count), [hourlyGenerationStatsData])

  useEffect(() => {
    if (!barChartRef.current) return;

    // 初始化图表实例
    if (!chartInstanceRef.current) {
      chartInstanceRef.current = echarts.init(barChartRef.current);
    }

    // 图表配置
    const option: echarts.EChartsOption = {
      // title: {
      //   text: '24小时生成趋势',
      //   left: 'center',
      //   textStyle: {
      //     fontSize: 16,
      //     fontWeight: 'normal'
      //   }
      // },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: hours,
        axisLabel: {
          color: '#666'
        },
        axisLine: {
          lineStyle: {
            color: '#e8e8e8'
          }
        },
        axisTick: {
          show: false
        }
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          color: '#666'
        },
        axisLine: {
          show: false
        },
        axisTick: {
          show: false
        },
        splitLine: {
          lineStyle: {
            color: '#f0f0f0',
            type: 'dashed'
          }
        }
      },
      series: [
        {
          type: 'bar',
          data: hourValues,
          barWidth: '60%',
          itemStyle: {
            color: '#7F56D9',
            borderRadius: [4, 4, 0, 0]
          },
          emphasis: {
            itemStyle: {
              color: '#6941C6' // hover的背景色
            }
          }
        }
      ]
    };

    chartInstanceRef.current.setOption(option);

    // 响应式调整
    const handleResize = () => {
      chartInstanceRef.current?.resize();
    };

    window.addEventListener('resize', handleResize);

    // 清理函数
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [hourlyGenerationStatsData]);

  useEffect(() => {
    const unsubscribe = agentDataOverviewManager.onHourlyGenerationStatsChange((e) => {
      setHourlyGenerationStatsData(e.detail)
    })
    return unsubscribe
  }, [])

  // 组件卸载时销毁图表实例
  useEffect(() => {
    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.dispose();
        chartInstanceRef.current = null;
      }
    };
  }, []);

  return {
    loading,
    barChartRef
  };
};

// 活跃用户TOP10数据
const useActiveTopUsers = () => {
  const [activeTopUsers, setActiveTopUsers] = useState(agentDataOverviewManager.getActiveTopUsers())

  useEffect(() => {
    const unsubscribe = agentDataOverviewManager.onActiveTopUsersChange((e) => {
      setActiveTopUsers(e.detail)
    })
    return unsubscribe
  }, [])

  return {
    activeTopUsers
  }
}

export const agentDataOverviewHooks = {
  useDataAnalysis,
  useUserStatsOverviewData,
  useLineChart,
  usePieChart,
  useBarChart,
  useActiveTopUsers,
}

// 导出单例实例
export const agentDataOverviewManager = AgentDataOverviewManager.getInstance();
